/**
 * Reading a lead's email conversation out of the sales mailbox.
 *
 * Kyu: "the ability to see email thread is for everyone." And, showing the
 * HubSpot contact page: "make sure the UI design is nice and well placed so
 * just like in hubspot it's easy to read the email threads."
 *
 * Two halves. The block between the sentinels turns what Gmail returns into
 * plain messages — addresses parsed, bodies decoded, the quoted history split
 * off — and is copied verbatim into supabase/functions/lead-mail-thread, where
 * it runs. mailThreadContract.test.ts holds the two copies identical. Below
 * the sentinels is the browser's half: grouping into threads and the labels
 * the cards show.
 *
 * ── Text, never HTML ───────────────────────────────────────────────────────
 *
 * A prospect's reply arrives as whatever their mail client produced, and an
 * inbound HTML email rendered inside the Hub is a script from a stranger
 * running on a page that holds a session. So the function reduces every
 * message to text — the text/plain part when there is one, which is nearly
 * always, and the HTML with its tags stripped when there is not — and the
 * browser renders text. Links are made clickable from the text, not trusted
 * from the markup. What is lost is bold and images; what is kept is safety,
 * and a conversation reads fine as text (Gmail's own "show original" is proof).
 *
 * ── Nothing is stored ──────────────────────────────────────────────────────
 *
 * docs/sales-inbox-design-note.md, item 5. Fetched when the Emails tab opens,
 * held in the page, thrown away. No Postgres table, no insert anywhere in the
 * function, and the contract test asserts that absence.
 */

// ── shared mail-thread rules: keep identical with supabase/functions/lead-mail-thread ──

export interface MailAddress {
  name: string;
  email: string;
}

export interface MailAttachment {
  name: string;
  mimeType: string;
  size: number;
}

export interface MailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: MailAddress;
  to: MailAddress[];
  cc: MailAddress[];
  /** ISO instant, from Gmail's internalDate. */
  date: string;
  /** `out` left the sales mailbox; `in` arrived in it. */
  direction: 'out' | 'in';
  /** The new words in this message. */
  body: string;
  /** The earlier messages this one quoted below its own words, if any. */
  quoted: string;
  attachments: MailAttachment[];
}

/** What Gmail returns for one MIME part with format=full. */
export interface GmailPart {
  mimeType?: string;
  filename?: string;
  headers?: Array<{ name: string; value: string }>;
  body?: { data?: string; size?: number; attachmentId?: string };
  parts?: GmailPart[];
}

/** What Gmail returns for one message with format=full. */
export interface GmailMessage {
  id?: string;
  threadId?: string;
  labelIds?: string[];
  internalDate?: string;
  payload?: GmailPart;
}

/**
 * `"Julia Shapiro" <julia@aloha.com>` → name and address. A bare address has
 * an empty name; the caller shows the address in its place.
 */
export function parseAddress(raw: string): MailAddress {
  const trimmed = raw.trim();
  const angled = /^"?([^"<]*?)"?\s*<([^>]+)>$/.exec(trimmed);
  if (angled) {
    return { name: (angled[1] ?? '').trim(), email: (angled[2] ?? '').trim().toLowerCase() };
  }
  return { name: '', email: trimmed.toLowerCase() };
}

/**
 * A To or Cc header. Split on commas that are not inside quotes or angle
 * brackets — `"Haskell, Brooke" <b@x.com>, a@y.com` is two people, not three.
 */
export function parseAddressList(raw: string): MailAddress[] {
  const found: MailAddress[] = [];
  let current = '';
  let inQuotes = false;
  let inAngle = false;
  for (const char of raw) {
    if (char === '"') inQuotes = !inQuotes;
    else if (char === '<' && !inQuotes) inAngle = true;
    else if (char === '>' && !inQuotes) inAngle = false;
    if (char === ',' && !inQuotes && !inAngle) {
      if (current.trim() !== '') found.push(parseAddress(current));
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim() !== '') found.push(parseAddress(current));
  return found.filter((address) => address.email !== '');
}

/** Gmail's body encoding: base64url, no padding, UTF-8 underneath. */
export function decodeBase64Url(data: string): string {
  const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * HTML to the text a person would have read. Used only when a message has no
 * text/plain part. Block elements become line breaks, everything else is
 * dropped, and the handful of entities mail clients actually emit are decoded.
 */
export function htmlToText(html: string): string {
  return html
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote|table)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * The line where a reply stops being new words and starts quoting the
 * earlier messages. Gmail, Apple Mail, Outlook and Thunderbird each mark it
 * differently; these are the marks they use. The earliest one wins.
 */
const QUOTE_MARKS: RegExp[] = [
  // Gmail, Apple Mail: "On Tue, 2 Sep 2026 at 10:14, Alex Schulze <…> wrote:",
  // sometimes wrapped over two lines.
  /^On [^\n]{0,300}(?:\n[^\n]{0,200})?wrote:\s*$/m,
  // Outlook desktop.
  /^-{2,}\s*Original Message\s*-{2,}\s*$/im,
  // Outlook web and mobile: a header block starting "From:".
  /^From:\s[^\n]+\n(?:Sent|Date):\s/m,
  // Outlook's rule between reply and history.
  /^_{5,}\s*$/m,
  // Anything already prefixed the classic way.
  /^>/m,
];

export function splitQuoted(text: string): { body: string; quoted: string } {
  let cut = -1;
  for (const mark of QUOTE_MARKS) {
    const match = mark.exec(text);
    if (match && (cut === -1 || match.index < cut)) cut = match.index;
  }
  if (cut === -1) return { body: text.trim(), quoted: '' };
  return { body: text.slice(0, cut).trim(), quoted: text.slice(cut).trim() };
}

/**
 * A line that is only a placeholder for an image. Gmail's text/plain twin of
 * an HTML mail writes every embedded picture as `[image: Alt text]`, usually
 * with the link it carried after it. We never render images, so the
 * placeholder is a note about something the reader cannot see either way.
 */
const IMAGE_LINE = /^\s*\[image:[^\]]*\]\s*(?:<[^>]*>)?\s*$/;

/** The RFC 3676 delimiter. Everything after a line of `--` is a signature. */
const SIG_DASH = /^--\s*$/;

/**
 * A line with no readable words in it — a bare address, a bare link, a job
 * title, a social icon. Everything a signature is made of.
 *
 * Four is the threshold because a signature's longest real line is a job
 * title and a company ("CEO, Co-Founder" · "Veritiv, Senior Buyer"), while
 * the shortest sentence somebody would be sorry to lose is about that long.
 * Links, addresses and image placeholders are removed before counting, so a
 * line that is nothing but a URL counts as zero words rather than one.
 */
function isFurniture(line: string): boolean {
  const words = line
    .replace(/\[image:[^\]]*\]/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/\S+@\S+\.\S+/g, ' ')
    .replace(/[|·•,;:()[\]\-–—]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  return words.length < 4;
}

/**
 * The signature, gone.
 *
 * Kyu, 3 September, on a reply whose last two thirds were Alex's sign-off —
 * a headshot, five social icons, a banner and a tracking pixel, every one of
 * them a `[image: …]` line with a link beside it: "as much as possible we
 * omit the signature mess, we just need to know who sent it, and the email
 * itself." Who sent it is the From line, which the card already shows. So the
 * signature is removed rather than folded away like the quoted history —
 * there is nothing in it a reader would go looking for.
 *
 * TWO CUTS, EARLIEST WINS, and only one of them is a guess.
 *
 * A line of `--` is the standard delimiter and means what it says, so it cuts
 * unconditionally.
 *
 * An image placeholder cuts only when EVERY line after it is furniture. That
 * condition is the whole safety of this function: a screenshot in the middle
 * of a message is followed by the sentence explaining it, the sentence is not
 * furniture, and the cut does not happen. A signature is followed by nothing
 * but names, titles, addresses and icons, and it does.
 *
 * WHAT THIS COSTS, stated rather than discovered later: a message that ends
 * with a picture and no words after it loses the note that a picture was
 * there. The attachment list still shows the file. Judged the right trade,
 * because the alternative is every reply carrying six lines of icons.
 */
export function stripSignature(body: string): string {
  const lines = body.split('\n');
  let cut = -1;
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    if (SIG_DASH.test(line)) {
      cut = index;
      break;
    }
    if (
      IMAGE_LINE.test(line) &&
      lines.slice(index).every((rest) => rest.trim() === '' || isFurniture(rest))
    ) {
      cut = index;
      break;
    }
  }
  return (
    (cut === -1 ? lines : lines.slice(0, cut))
      // Any placeholder that survived the cut goes too. One in the middle of a
      // real paragraph is the same note about the same unshowable picture.
      .filter((line) => !IMAGE_LINE.test(line))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/** One header off a MIME part, case-insensitively, or ''. */
function partHeader(part: GmailPart, name: string): string {
  const found = (part.headers ?? []).find((h) => h.name.toLowerCase() === name.toLowerCase());
  return found?.value ?? '';
}

/**
 * An image the HTML referenced by `cid:` rather than a file somebody chose to
 * send. Both conditions are required: `Content-Disposition: inline` alone is
 * advisory and some clients put it on real attachments, but inline WITH a
 * Content-ID means the markup pointed at it, and the only markup we ever get
 * pointing at pictures is a signature.
 *
 * The cost, again stated: a screenshot dragged straight into the compose box
 * is embedded the same way and will not be listed. A file added with the
 * paperclip is `attachment`, has no Content-ID, and still shows.
 */
function isEmbeddedImage(part: GmailPart): boolean {
  return (
    partHeader(part, 'Content-Disposition').trim().toLowerCase().startsWith('inline') &&
    partHeader(part, 'Content-ID') !== ''
  );
}

/**
 * Walk the MIME tree once. The first text/plain and the first text/html are
 * kept; anything with a filename is an attachment, whatever its type. Plain
 * wins when both exist, because it is what the sender's client wrote for
 * exactly this purpose.
 */
export function bodyOf(payload: GmailPart): { text: string; attachments: MailAttachment[] } {
  let plain = '';
  let html = '';
  const attachments: MailAttachment[] = [];

  function walk(part: GmailPart) {
    const filename = part.filename ?? '';
    const mimeType = (part.mimeType ?? '').toLowerCase();
    if (filename !== '') {
      // The signature's logos and icons arrive here with UUID filenames and
      // were listed beside real files until 3 September.
      if (!isEmbeddedImage(part)) {
        attachments.push({ name: filename, mimeType, size: part.body?.size ?? 0 });
      }
      return;
    }
    const data = part.body?.data;
    if (typeof data === 'string' && data !== '') {
      if (mimeType === 'text/plain' && plain === '') plain = decodeBase64Url(data);
      else if (mimeType === 'text/html' && html === '') html = decodeBase64Url(data);
    }
    for (const child of part.parts ?? []) walk(child);
  }
  walk(payload);

  const text = plain !== '' ? plain : htmlToText(html);
  return { text: text.replace(/\r\n/g, '\n'), attachments };
}

/** One Gmail message, reduced to what the Hub shows. Null when it has no id. */
export function messageFromGmail(raw: GmailMessage, mailbox: string): MailMessage | null {
  if (typeof raw.id !== 'string' || typeof raw.threadId !== 'string') return null;
  const headers = raw.payload?.headers ?? [];
  const header = (name: string) =>
    headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value ?? '';

  const from = parseAddress(header('From'));
  const sentByMailbox =
    (raw.labelIds ?? []).includes('SENT') || from.email === mailbox.toLowerCase();
  const { text, attachments } = bodyOf(raw.payload ?? {});
  const { body, quoted } = splitQuoted(text);
  const stamp = Number(raw.internalDate ?? '');

  return {
    id: raw.id,
    threadId: raw.threadId,
    subject: header('Subject').trim() || '(no subject)',
    from,
    to: parseAddressList(header('To')),
    cc: parseAddressList(header('Cc')),
    date: new Date(Number.isFinite(stamp) && stamp > 0 ? stamp : 0).toISOString(),
    direction: sentByMailbox ? 'out' : 'in',
    // Quoted history off first, then the signature off what is left. In that
    // order, because a signature sits above the quote and stripSignature's
    // "everything after this is furniture" test would never be true with a
    // whole quoted conversation still attached below it.
    body: stripSignature(body),
    quoted,
    attachments,
  };
}

// ── end shared mail-thread rules ──

// ── The browser's half ─────────────────────────────────────────────────────

export interface MailThread {
  threadId: string;
  /** Oldest first, the way a conversation is read. */
  messages: MailMessage[];
  /** The newest message: what the collapsed card shows. */
  latest: MailMessage;
}

/**
 * Messages into conversations, newest conversation first. Mirrors HubSpot's
 * "Thread email replies": one card per conversation, its replies inside.
 */
export function groupThreads(messages: MailMessage[]): MailThread[] {
  const byThread = new Map<string, MailMessage[]>();
  for (const message of messages) {
    byThread.set(message.threadId, [...(byThread.get(message.threadId) ?? []), message]);
  }
  const threads: MailThread[] = [];
  for (const [threadId, list] of byThread) {
    const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
    const latest = sorted[sorted.length - 1];
    if (latest) threads.push({ threadId, messages: sorted, latest });
  }
  return threads.sort((a, b) => b.latest.date.localeCompare(a.latest.date));
}

/** Every message as its own card, newest first — "Thread email replies" off. */
export function ungroupThreads(messages: MailMessage[]): MailThread[] {
  return [...messages]
    .sort((a, b) => b.date.localeCompare(a.date))
    .map((message) => ({ threadId: message.id, messages: [message], latest: message }));
}

/** "April 2026", in the reader's timezone, for the heading above a month's cards. */
export function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** "Apr 17, 2026 at 12:37 AM", in the reader's timezone. */
export function whenLabel(iso: string): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

/** The name if the header carried one, else the address. */
export function displayName(address: MailAddress): string {
  return address.name !== '' ? address.name : address.email;
}

/** "Julia Shapiro", "Julia Shapiro and Richa Shah", "A, B and C". */
export function nameList(addresses: MailAddress[]): string {
  const names = addresses.map(displayName);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The first line or two of a body, for the collapsed card. */
export function previewOf(body: string): string {
  return body.replace(/\s+/g, ' ').trim();
}

/** The search box: subject, anybody's name or address, or the words. */
export function threadMatches(thread: MailThread, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return thread.messages.some((message) =>
    [
      message.subject,
      message.body,
      message.from.name,
      message.from.email,
      ...message.to.flatMap((a) => [a.name, a.email]),
      ...message.cc.flatMap((a) => [a.name, a.email]),
    ]
      .join('\n')
      .toLowerCase()
      .includes(needle),
  );
}

/** Split text so bare URLs can be rendered as links without trusting any markup. */
export function textWithLinks(text: string): Array<{ text: string; href?: string }> {
  const pieces: Array<{ text: string; href?: string }> = [];
  let last = 0;
  for (const match of text.matchAll(/https?:\/\/[^\s<>"]+/g)) {
    const raw = match[0];
    const trimmed = raw.replace(/[.,;:!?)]+$/, '');
    if (match.index > last) pieces.push({ text: text.slice(last, match.index) });
    pieces.push({ text: trimmed, href: trimmed });
    last = match.index + trimmed.length;
  }
  if (last < text.length) pieces.push({ text: text.slice(last) });
  return pieces;
}

/** "Re: " once, however many times the subject has already been replied to. */
export function replySubject(subject: string): string {
  const trimmed = subject.trim();
  if (trimmed === '' || trimmed === '(no subject)') return 'Re:';
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

/** Attachment sizes the way a mail client shows them. */
export function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The refusals lead-mail-thread can return, translated for the person reading
 * them. Same shape as sendErrorMessage in useMailbox.ts.
 */
export function threadErrorMessage(code: string, detail?: unknown): string {
  switch (code) {
    case 'not_signed_in':
      return 'Your session has expired. Sign out and back in.';
    case 'lead_not_found':
      return 'This lead no longer exists, or you cannot see it.';
    case 'lead_has_no_email':
      return 'This lead has no email address, so there is no conversation to show.';
    case 'lead_on_hold': {
      const hold = detail !== null && typeof detail === 'object' ? detail : {};
      const until = 'until' in hold && typeof hold.until === 'string' ? hold.until : null;
      const note = 'note' in hold && typeof hold.note === 'string' ? hold.note.trim() : '';
      const message =
        until === null
          ? 'This lead is on hold, so nothing was sent.'
          : `This lead is on hold until ${until}, so nothing was sent.`;
      return note === '' ? message : `${message} [${note}]`;
    }
    case 'mailbox_not_connected':
    case 'reauthorise_needed':
      return 'The sales mailbox needs to be reconnected. Tell Kyu.';
    case 'read_not_permitted':
      return 'Gmail refused to show the mailbox: the connection is missing the read permission. Tell Kyu.';
    case 'gmail_refused':
      return 'Gmail did not answer. Try again in a moment.';
    default:
      return 'The conversation could not be loaded. Try again in a moment.';
  }
}
